import React, { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { ShieldCheck, Bot, Globe, History, FileCheck, Wand2 } from "lucide-react";
import { Position } from "reactflow"; // Added Position
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import { ClusterNodeData, HandleConfig } from "../../types"; // Added HandleConfig
import { ENVIRONMENTS, MODULES, ModuleConfig } from "../../types/cluster";
import useForm from "../../hooks/useForm";
import useStore from "../../store/useStore";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager

interface ClusterConfigBoxProps {
  nodeId: string; // Add nodeId prop
  data: ClusterNodeData;
  onUpdate?: (data: Partial<ClusterNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null; // Add portal root prop
}

// Team access options
const TEAM_ACCESS_OPTIONS = [
  { value: "direct", label: "Direct access (Recommended)" },
  { value: "no-access", label: "No access" },
  { value: "indirect", label: "Indirect but auditable access" },
  { value: "other", label: "Other" }
];

const CUSTOM_LABEL_SUGGESTIONS = [
  { value: "Production", label: "Production" },
  { value: "Development", label: "Development" },
  { value: "Staging", label: "Staging" },
  { value: "Beta", label: "Beta" },
  { value: "Internal", label: "Internal" },
  { value: "High Priority", label: "High Priority" },
  { value: "Low Priority", label: "Low Priority" },
  { value: "Critical", label: "Critical" },
  { value: "Experimental", label: "Experimental" },
  { value: "Legacy", label: "Legacy" },
  { value: "New", label: "New" },
  { value: "Deprecated", label: "Deprecated" },
  { value: "Custom", label: "Custom" },
];

// Icon mapping for modules
const ICON_MAP: Record<string, any> = {
  'shield-check': ShieldCheck,
  'robot': Bot,
  'globe': Globe,
  'refresh': History,
  'check-square': FileCheck,
  'transform': Wand2,
};

const getDefaultClusterHandles = (nodeId: string): HandleConfig[] => [
  { id: `cluster-${nodeId}-ingestor`, type: 'target', position: Position.Left, color: '#3B82F6' },
  { id: `cluster-${nodeId}-forwarders`, type: 'source', position: Position.Right, color: '#10B981' },
];

export default function ClusterConfigBox({
  nodeId, // Destructure nodeId
  data,
  onUpdate,
  onDuplicate,
  onDelete,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: ClusterConfigBoxProps) {
  const { formData, setFormData } = useForm<ClusterNodeData>({
    ...data,
    project: data.project || '',
    type: 'cluster',
    label: data.label || '',
    modules: data.modules || [],
    clusterEnvironment: data.clusterEnvironment || {
      environmentId: '',
      name: '',
    }
  });

  // Get latency impact from store
  const [latencyImpact, setLatencyImpact] = useState<number | null>(data.totalLatencyImpact || null);
  
  // Initialize state for custom fields and visibility
  const [teamAccess, setTeamAccess] = useState(data.teamAccess || "direct");
  const [otherTeamAccess, setOtherTeamAccess] = useState(data.otherTeamAccess || "");
  const [customLabel, setCustomLabel] = useState(data.customLabel || "");
  
  // Initialize visibility settings
  const [environmentVisibility, setEnvironmentVisibility] = useState<VisibilityOption>(
    data.environmentVisibility === "hidden" ? "hidden" : "visible"
  );
  const [teamAccessVisibility, setTeamAccessVisibility] = useState<VisibilityOption>(
    data.teamAccessVisibility !== undefined ? data.teamAccessVisibility : "hidden"
  );
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.customLabelVisibility === "hidden" ? "hidden" : "visible"
  );
  
  // Initialize module visibility settings
  const [moduleVisibility, setModuleVisibility] = useState<VisibilityOption>(
    data.moduleVisibility === "hidden" ? "hidden" : "visible"
  );

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() =>
    Array.isArray(data.handles) ? [...data.handles] : getDefaultClusterHandles(nodeId) // Use nodeId prop here
  );
  // --- End Add State for Handles ---

  // Update latency impact when it changes in the store
  useEffect(() => {
    // Subscribe to store changes to get updated latency values
    const unsubscribe = useStore.subscribe(
      (state) => {
        // Get the current cluster latencies
        const clusterLatencies = state.clusterLatencies;
        
        // Find the node ID from the selected node
        const selectedNode = state.selectedNode;
        if (selectedNode && selectedNode.type === 'cluster' && clusterLatencies[selectedNode.id] !== undefined) {
          setLatencyImpact(clusterLatencies[selectedNode.id]);
        }
      }
    );
    
    // Initial fetch of latency impact
    const state = useStore.getState();
    const selectedNode = state.selectedNode;
    if (selectedNode && selectedNode.type === 'cluster' && state.clusterLatencies[selectedNode.id] !== undefined) {
      setLatencyImpact(state.clusterLatencies[selectedNode.id]);
    }
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Update parent component when form data changes
  const previousDataRef = useRef<string>("");
  const isInitialized = useRef(false); // Track initialization

  useEffect(() => {
    // Prevent firing on initial mount/effect synchronization
    if (!isInitialized.current) {
      // We'll set this after the first sync effect runs
      return;
    }

    if (onUpdate) {
      const updatedData: Partial<ClusterNodeData> = {
        type: 'cluster',
        label: formData.label,
        clusterEnvironment: formData.clusterEnvironment,
        modules: formData.modules,
        totalLatencyImpact: latencyImpact,
        teamAccess,
        otherTeamAccess,
        customLabel,
        environmentVisibility,
        teamAccessVisibility,
        customLabelVisibility,
        moduleVisibility,
        handles: Array.isArray(handles) ? handles : [], // Add handles to update
      };

      // Stringify current data for comparison
      const currentDataString = JSON.stringify({
        label: updatedData.label,
        clusterEnvironment: updatedData.clusterEnvironment,
        modules: updatedData.modules,
        totalLatencyImpact: updatedData.totalLatencyImpact,
        teamAccess,
        otherTeamAccess,
        customLabel,
        environmentVisibility,
        teamAccessVisibility,
        customLabelVisibility,
        moduleVisibility,
        handles: updatedData.handles, // Include handles in comparison string
      });

      // Only update if data has changed
      if (currentDataString !== previousDataRef.current) {
        previousDataRef.current = currentDataString;
        
        // Call onUpdate with the updated data
        onUpdate(updatedData);
      }
    }
  }, [
    formData.label, 
    formData.clusterEnvironment, 
    formData.modules, 
    latencyImpact, 
    teamAccess,
    otherTeamAccess,
    customLabel,
    environmentVisibility,
    teamAccessVisibility,
    customLabelVisibility,
    moduleVisibility,
    handles, // Add handles dependency
    onUpdate
  ]);
  
  // Sync internal state with data prop changes (e.g., when node is reselected or project loads)
  useEffect(() => {
    // Always update the core form data when the `data` prop changes.
    setFormData({
      ...data, // Start with the incoming data
      project: data.project || '', // Ensure defaults
      type: 'cluster',
      label: data.label || '',
      modules: data.modules || [],
      clusterEnvironment: data.clusterEnvironment || {
        environmentId: '',
        name: '',
      }
    });

    // Update latency impact
    setLatencyImpact(data.totalLatencyImpact || null);

    // Update custom fields and visibility settings directly from data prop
    setTeamAccess(data.teamAccess || "direct");
    setOtherTeamAccess(data.otherTeamAccess || "");
    setCustomLabel(data.customLabel || "");
    setEnvironmentVisibility(data.environmentVisibility === "hidden" ? "hidden" : "visible");
    setTeamAccessVisibility(data.teamAccessVisibility !== undefined ? data.teamAccessVisibility : "hidden");
    setCustomLabelVisibility(data.customLabelVisibility === "hidden" ? "hidden" : "visible");
    setModuleVisibility(data.moduleVisibility === "hidden" ? "hidden" : "visible");

    // Sync handles state: Update only if the incoming handles are different
    // Use a deep comparison or stringify for simplicity here if needed
    const incomingHandlesString = JSON.stringify(data.handles || []);
    const currentHandlesString = JSON.stringify(handles);
    if (Array.isArray(data.handles) && incomingHandlesString !== currentHandlesString) {
        setHandles([...data.handles]);
    } else if (!data.handles && handles.length > 0) {
        // If incoming data has no handles, but we have some, potentially reset or keep?
        // For now, let's keep existing ones unless explicitly overwritten by data.handles
        // If default handles are needed when data.handles is missing, add logic here.
        // Example: setHandles(getDefaultClusterHandles((data as any).id || 'cluster-node'));
    }

    // Mark as initialized *after* the first sync based on data prop completes.
    // This ensures the effect calling onUpdate doesn't fire prematurely.
    if (!isInitialized.current) {
        isInitialized.current = true;
    }

    // --- Simplified Handle Syncing ---
    // Determine handles based on incoming data or defaults for the *current* node ID (using the prop)
    const incomingHandles = data.handles || getDefaultClusterHandles(nodeId); // Use nodeId prop here
    // Ensure we always set a valid array
    setHandles(Array.isArray(incomingHandles) ? [...incomingHandles] : getDefaultClusterHandles(nodeId));
    // --- End Simplified Handle Syncing ---


    // Mark as initialized *after* the first sync based on data prop completes.
    // This ensures the effect calling onUpdate doesn't fire prematurely.
    if (!isInitialized.current) {
        isInitialized.current = true;
    }

  }, [data, setFormData]); // Re-run this effect when data prop changes, added setFormData for exhaustive-deps

  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  // Toggle module visibility
  const toggleModuleVisibility = () => {
    setModuleVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);
  // --- End Handler ---


  const handleChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let newData: ClusterNodeData = { ...formData };

      if (field === "environment") {
        const selectedEnv = ENVIRONMENTS.find(env => env.id === event.target.value);
        if (selectedEnv) {
          newData.clusterEnvironment = {
            environmentId: selectedEnv.id,
            name: selectedEnv.name,
            hintText: selectedEnv.hintText,
          };
        }
      } else {
        newData = {
          ...formData,
          [field]: event.target.value,
        };
      }

      setFormData(newData);
    };

  const toggleModule = (moduleId: string) => {
    const currentModules = formData.modules || [];
    const moduleIndex = currentModules.findIndex(m => m.moduleId === moduleId);
    let newModules: ModuleConfig[];

    if (moduleIndex >= 0) {
      // Toggle existing module
      newModules = currentModules.map((m, i) => 
        i === moduleIndex ? { ...m, enabled: !m.enabled } : m
      );
    } else {
      // Add new module
      newModules = [...currentModules, { moduleId, enabled: true }];
    }

    const newData: ClusterNodeData = {
      ...formData,
      modules: newModules,
    };

    setFormData(newData);
  };

  const isModuleEnabled = (moduleId: string): boolean => {
    return formData.modules?.some(m => m.moduleId === moduleId && m.enabled) || false;
  };
  

  return (
    <div className="space-y-3 relative">
      {/* Action toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Cluster Name
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={handleChange("label")}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Environment"
          visibility={environmentVisibility}
          onToggleVisibility={() => toggleVisibility(environmentVisibility, setEnvironmentVisibility)}
          className="mb-1 mt-4"
        />
        <select
          value={formData.clusterEnvironment?.environmentId || ""}
          onChange={handleChange("environment")}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        >
          <option value="">Select environment</option>
          {ENVIRONMENTS.filter(env => env.isEnabled).map((env) => (
            <option key={env.id} value={env.id}>
              {env.name}
            </option>
          ))}
        </select>
      </div>
      
      {/* MetaRouter Team Access */}
      <div>
        <LabelWithVisibilityToggle
          label="MetaRouter Team Access"
          visibility={teamAccessVisibility}
          onToggleVisibility={() => toggleVisibility(teamAccessVisibility, setTeamAccessVisibility)}
          className="mb-1 mt-4"
        />
        <select
          value={teamAccess}
          onChange={(e) => setTeamAccess(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        >
          {TEAM_ACCESS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      
      {/* Other Team Access (only shown when "Other" is selected) */}
      {teamAccess === "other" && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Specify Team Access
          </label>
          <input
            type="text"
            value={otherTeamAccess}
            onChange={(e) => setOtherTeamAccess(e.target.value)}
            placeholder="Specify team access details"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
          />
        </div>
      )}
      
      {/* Custom Label */}
      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={() => toggleVisibility(customLabelVisibility, setCustomLabelVisibility)}
          className="mb-1 mt-4"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Add custom label"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      {/* Modules */}
      <div>
        <LabelWithVisibilityToggle
          label="Enabled Modules"
          visibility={moduleVisibility}
          onToggleVisibility={toggleModuleVisibility}
          className="mt-4 mb-2"
        />
        <div className="space-y-1">
          {MODULES.filter(module => module.isEnabled).map((module) => {
            const Icon = ICON_MAP[module.icon || ''] || ShieldCheck;
            const isEnabled = isModuleEnabled(module.id);

            return (
              <div key={module.id} className="flex">
                <button
                  onClick={() => toggleModule(module.id)}
                  className={`
                    flex-grow flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm border border-gray-200 dark:border-gray-700
                    ${isEnabled 
                      ? "bg-blue-100 border-blue-300 dark:bg-blue-500/20 hover:dark:border-blue-500/30 text-blue-900 dark:text-blue-100" 
                      : "hover:bg-gray-50 border-blue-500 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-300"}
                  `}
                >
                  <Icon
                    className={`h-4 w-4 flex-shrink-0 ${
                      isEnabled ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{module.name}</div>
                    <div
                      className={`text-xs truncate ${
                        isEnabled ? "text-blue-700 dark:text-blue-300" : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {module.description}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Render Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId} // Use the nodeId prop
          handles={handles} // Use handles state
          onChange={handleHandlesChange}
          maxHandles={6} // Adjust as needed
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}
